/**
 * DistributionStep - Step 3 of Add Budget Dialog: Distribute budget across units
 */

import React from "react";
import { toast } from "react-toastify";
import { Cube, Percent, Ruler, ArrowLeft } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { UnitDTO } from "shared/types/unit";
import { formatCurrency, UnitDistribution } from "../types";

interface DistributionStepProps {
  unitIds: string[];
  amount: number;
  unitDistribution: UnitDistribution;
  setUnitDistribution: (dist: UnitDistribution) => void;
  buildingUnits: UnitDTO[];
  onBack: () => void;
}

export const DistributionStep: React.FC<DistributionStepProps> = ({
  unitIds,
  amount,
  unitDistribution,
  setUnitDistribution,
  buildingUnits,
  onBack,
}) => {
  const getUnitDetails = (unitId: string) => buildingUnits.find((u) => u.id === unitId);

  const totalPercentage = Object.values(unitDistribution).reduce((sum, pct) => sum + (pct || 0), 0);
  const isValid = Math.abs(totalPercentage - 100) < 0.01;

  const handleChange = (unitId: string, value: number) => {
    setUnitDistribution({
      ...unitDistribution,
      [unitId]: Math.max(0, Math.min(100, value)),
    });
  };

  const setEqualDistribution = () => {
    const equalPct = 100 / unitIds.length;
    const distribution: UnitDistribution = {};
    let runningTotal = 0;

    unitIds.forEach((id, index) => {
      if (index === unitIds.length - 1) {
        // Last unit gets the remainder to ensure exactly 100%
        distribution[id] = Math.round((100 - runningTotal) * 100) / 100;
      } else {
        const rounded = Math.round(equalPct * 100) / 100;
        distribution[id] = rounded;
        runningTotal += rounded;
      }
    });
    setUnitDistribution(distribution);
  };

  const setSurfaceDistribution = () => {
    const totalSurface = unitIds.reduce((sum, id) => {
      const unit = getUnitDetails(id);
      return sum + (unit?.netSurface || unit?.grossSurface || 0);
    }, 0);

    if (totalSurface === 0) {
      toast.error("Geen oppervlakte data beschikbaar");
      return;
    }

    const distribution: UnitDistribution = {};
    let runningTotal = 0;

    unitIds.forEach((id, index) => {
      const unit = getUnitDetails(id);
      const surface = unit?.netSurface || unit?.grossSurface || 0;

      if (index === unitIds.length - 1) {
        // Last unit gets the remainder to ensure exactly 100%
        distribution[id] = Math.round((100 - runningTotal) * 100) / 100;
      } else {
        const rounded = Math.round((surface / totalSurface) * 10000) / 100;
        distribution[id] = rounded;
        runningTotal += rounded;
      }
    });
    setUnitDistribution(distribution);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Button type="button" variant="ghost" size="sm" onClick={onBack} className="text-xs">
          <ArrowLeft className="mr-1 h-4 w-4" />
          Terug
        </Button>
        <div className="text-right">
          <p className="text-sm font-medium text-moss-primary">{formatCurrency(amount)}</p>
          <p className="text-xs text-neutral-500">Te verdelen</p>
        </div>
      </div>

      <div className="rounded-md bg-neutral-50 p-3">
        <div className="flex items-center gap-2 text-sm font-medium text-neutral-700">
          <Percent className="h-4 w-4 text-moss-primary" />
          Verdeling per kostenplaats
        </div>
        <p className="mt-1 text-xs text-neutral-500">
          Verdeel het budget over de geselecteerde kostenplaatsen. De percentages moeten optellen
          tot 100%.
        </p>
      </div>

      <div className="space-y-3">
        {unitIds.map((unitId) => {
          const unit = getUnitDetails(unitId);
          const percentage = unitDistribution[unitId] || 0;
          const calculatedAmount = (amount * percentage) / 100;
          const surface = unit?.netSurface || unit?.grossSurface || 0;
          const eurPerM2 = surface > 0 ? calculatedAmount / surface : 0;

          return (
            <div key={unitId} className="rounded-md border border-neutral-200 bg-white p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Cube className="h-4 w-4 text-moss-primary" />
                    <span className="font-medium text-neutral-700">{unitId}</span>
                  </div>
                  {surface > 0 && (
                    <div className="mt-1 flex items-center gap-1 text-xs text-neutral-500">
                      <Ruler className="h-3 w-3" />
                      {surface.toFixed(0)} m²
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative w-20">
                    <Input
                      type="number"
                      value={percentage || ""}
                      onChange={(e) => {
                        const value = e.target.value === "" ? 0 : parseFloat(e.target.value);
                        handleChange(unitId, isNaN(value) ? 0 : value);
                      }}
                      className="pr-6 text-right"
                      min={0}
                      max={100}
                      step={0.5}
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-sm text-neutral-400">
                      %
                    </span>
                  </div>
                </div>
              </div>
              <div className="mt-2 flex items-center justify-between border-t border-neutral-100 pt-2">
                <span className="text-sm font-medium text-moss-primary">
                  {formatCurrency(calculatedAmount)}
                </span>
                {surface > 0 && calculatedAmount > 0 && (
                  <span className="text-xs text-neutral-500">{formatCurrency(eurPerM2)}/m²</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div
        className={`flex items-center justify-between rounded-md p-3 ${
          isValid ? "border border-green-200 bg-green-50" : "border border-amber-200 bg-amber-50"
        }`}
      >
        <span className="text-sm font-medium">Totaal:</span>
        <span className={`text-sm font-bold ${isValid ? "text-green-700" : "text-amber-700"}`}>
          {totalPercentage.toFixed(1)}%
          {!isValid && <span className="ml-2 text-xs font-normal">(moet 100% zijn)</span>}
        </span>
      </div>

      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={setEqualDistribution}
          className="text-xs"
        >
          Gelijke verdeling
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={setSurfaceDistribution}
          className="text-xs"
        >
          <Ruler className="mr-1 h-3 w-3" />
          Verdelen op m²
        </Button>
      </div>
    </div>
  );
};

export default DistributionStep;
